import { trackedObject } from '@ember/reactive/collections';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import dayjs from 'dayjs';
import { task, type TaskInstance } from 'ember-concurrency';
import type { Hotel } from 'frontend-utils/models';
import type HotelEventsController from 'lumen/controllers/hotel/events';
import type { Day } from 'lumen/services/repositories/day';
import type { Transition } from 'lumen/services/transition';
import { getPeriodsInRange } from 'lumen/utils/dates/moment-helpers';
import type { ModelFrom } from 'lumen/utils/routes';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

type EventsRouteParams = {
  month: string;
  refresh: boolean;
};

type EventsRouteModel = {
  events: Partial<TaskInstance<Day[]>>;
  holidays: Partial<TaskInstance<Day[]>>;
};

export default class HotelEventsRoute extends Route {
  queryParams = {
    month: {
      refreshModel: true,
    },
    refresh: {
      refreshModel: true,
    },
    highlight: {
      refreshModel: false,
    },
  };

  @service declare access: Services['access'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare navigation: Services['navigation'];
  @service('repositories/day')
  declare repositoriesDay: Services['repositories/day'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  modelObject = trackedObject<EventsRouteModel>({
    events: {
      isError: false,
      isRunning: false,
      value: [] as Day[] | null,
    },
    holidays: {
      isError: false,
      isRunning: false,
    },
  });

  fetchEventsTask = task(
    { restartable: true },
    async ({
      formattedDateRangeStart,
      formattedDateRangeEnd,
      startUnix,
      endUnix,
    }: {
      endUnix: number;
      formattedDateRangeEnd: string;
      formattedDateRangeStart: string;
      startUnix: number;
    }) => {
      try {
        const days = await this.repositoriesDay.findEventsByDateRange({
          hotelId: (this.modelFor('hotel') as Hotel).id!,
          startDate: formattedDateRangeStart,
          endDate: formattedDateRangeEnd,
        });

        return days.filter(
          (x) => x.integerDate >= startUnix && x.integerDate <= endUnix,
        );
      } catch (error) {
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  fetchHolidaysTask = task(
    { restartable: true },
    async ({
      formattedDateRangeStart,
      formattedDateRangeEnd,
    }: {
      formattedDateRangeEnd: string;
      formattedDateRangeStart: string;
    }) => {
      try {
        return this.repositoriesDay.findHolidaysByDateRange({
          hotelId: (this.modelFor('hotel') as Hotel).id!,
          startDate: formattedDateRangeStart,
          endDate: formattedDateRangeEnd,
        });
      } catch (error) {
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  afterModel(): void {
    const params = this.paramsFor(this.routeName) as EventsRouteParams;
    if (params.refresh) {
      this.router.transitionTo({ queryParams: { refresh: false } });
    }
  }

  beforeModel(transition: Transition): void {
    if (this.navigation.redirectToAccountSelection(transition)) {
      return;
    }

    if (!this.access.hasEvents) {
      this.transition.redirectToEnabledTab(transition);
    } else {
      this.transition.validateEventsQueryParams(
        transition,
        this.paramsFor(this.routeName) as Record<string, string>, // Use paramsFor to obtain the deserialised query params
      );
    }
  }

  deactivate(): void {
    /**
     * In application tests, the app gets torn down after each test. This causes the store to be destroyed
     * before the deactivate hook is called. This check is to prevent the store from being accessed after it's destroyed.
     */
    if (this.store.isDestroyed) {
      return;
    }
    this.store.unloadAll('event');
    this.store.unloadAll('holiday');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  model(params: EventsRouteParams) {
    const month = this.hotelAndMarketService.toLocalTime(`${params.month}-15`);
    const dateRangeStart = month.startOf('month').startOf('week');
    const dateRangeEnd = month.endOf('month').endOf('week').startOf('day');
    const formattedDateRangeStart = dateRangeStart.format('YYYY-MM-DD');
    const formattedDateRangeEnd = dateRangeEnd.format('YYYY-MM-DD');
    // Unix timestamp needs to be based on UTC date
    const startUnix = Math.floor(
      dayjs.utc(formattedDateRangeStart).unix() / 86400,
    );
    const endUnix = Math.floor(dayjs.utc(formattedDateRangeEnd).unix() / 86400);

    // Pre-create dummy Day records before the task starts so they already exist
    // when the loading state renders. This avoids calling store.createRecord()
    // inside a reactive getter (which causes a Glimmer autotracking violation).
    getPeriodsInRange(dateRangeStart, dateRangeEnd, 'day').forEach((period) => {
      const id = period.format();
      if (!this.repositoriesDay.peekByDate(id)) {
        this.repositoriesDay.add({ id, date: period });
      }
    });

    this.modelObject.events = this.fetchEventsTask.perform({
      formattedDateRangeStart,
      formattedDateRangeEnd,
      startUnix,
      endUnix,
    });

    this.modelObject.holidays = this.fetchHolidaysTask.perform({
      formattedDateRangeStart,
      formattedDateRangeEnd,
    });

    return this.modelObject;
  }

  // @ts-expect-error: Property 'setupController' in type 'EventsRoute' is not assignable to the same property in base type 'Route'.

  setupController(
    controller: HotelEventsController,
    // @ts-expect-error: Type 'EventsRoute' does not satisfy the constraint 'Route<unknown>'.
    model: ModelFrom<EventsRoute>,
    transition: Transition,
  ): void {
    // @ts-expect-error: Argument of type 'HotelEventsController' is not assignable to parameter of type 'Controller<unknown>'.
    super.setupController(controller, model, transition);
    if (controller.refresh) {
      controller.refresh = false;
    }
  }
}
